import * as React from 'react';
import axios from 'axios';
import { BASE_URL } from '../../../../config/environmentConfig';
import download from 'downloadjs';
import { paymentsActionTypes } from '../payments-action-types';
import { sendSentryMessage } from '../../../utils/sentry-helper';
import { IPaymentsAction, IPaymentsContextState } from '../payments-interfaces';
import { removeNullValues } from '../../../utils/utilities';
import { errorAlert } from '../../../components/ToastMessage';
import {
  getFormattedAdyenProviderFields,
  getFormattedBraintreeProviderFields,
  getFormattedCheckoutProviderFields, getFormattedUsioProviderFields,
  getFormattedNmiProviderFields,
  getFormattedPaysafeProviderFields,
  getFormattedRapydProviderFields, getFormattedRevolv3ProviderFields,
  PROVIDER,
  providerFormReturnTypes,
} from '../../../constants/provider/provider-form';
import { GET, POST, PUT } from '../../store-helper';
import { IProviderFiltersProps } from '../../../components/provider/ProviderFilters';

export const fetchPaymentGatewayData = async ({ dispatch, mid }): Promise<void> => {
  try {
    dispatch({ type: paymentsActionTypes.START_PAYMENT_GATEWAY_DATA_LOADING });
    const data = await GET({ pathname: `${BASE_URL.PAYMENTS}/gateway?mid=${mid}` });

    dispatch({ type: paymentsActionTypes.PAYMENT_GATEWAY_DATA, payload: data ?? [] });
  } catch (e) {
    sendSentryMessage({ name: `GET Fetch PaymentGateway data error`, context: e });
    console.log(e);
    dispatch({ type: paymentsActionTypes.FETCH_LOADING_FAIL });
  }
};

const getProviderQueryParams = (state: IPaymentsContextState): { [p: string]: string | number } => {
  const params: { [key: string]: string | number } = {};

  if (state?.isActive) {
    params.isActive = state?.isActive === '1' ? 'true' : 'false';
  }

  if (state?.partnerId) {
    params.partnerId = state?.partnerId;
  }

  return params;
};

interface IProviderDataProps {
  dispatch: React.Dispatch<IPaymentsAction>,
  searchParams: IProviderFiltersProps
}

export const fetchProviderData = async ({ dispatch, searchParams }: IProviderDataProps): Promise<void> => {
  try {
    dispatch({ type: paymentsActionTypes.SET_PROVIDER_DATA_LOADING, payload: true });

    const filteredUrl = removeNullValues({ ...searchParams });

    const data = await GET({
      pathname: `${BASE_URL.PAYMENTS}/providers`,
      params: filteredUrl
    });

    dispatch({ type: paymentsActionTypes.FETCH_PROVIDER_DATA, payload: data || [] });
  } catch (e) {
    sendSentryMessage({ name: `GET Fetch provider data error`, context: e });
    console.log('fetchProviderData error:', e);
    dispatch({ type: paymentsActionTypes.FETCH_PROVIDER_DATA, payload: [] });
  }
};

export const setPaymentGateway = async (params): Promise<void> => {
  try {
    await PUT({ pathname: `${BASE_URL.PAYMENTS}/gateway`, body: params });
  } catch (e) {
    sendSentryMessage({ name: `PUT Set PaymentGateway error`, context: e });
    console.log(e);
  }
};

interface ISetProviderData {
  dispatch: React.Dispatch<IPaymentsAction>,
  params: any,
  setError?: (value: any) => void,
  fieldName?: string,
}

export const setProviders = async ({ dispatch, params, setError }: ISetProviderData): Promise<void> => {

  try {
    await axios.put(`${BASE_URL.PAYMENTS}/providers`, params);
  } catch (e) {
    sendSentryMessage({ name: `PUT Set provider error`, context: e });
    dispatch({ type: paymentsActionTypes.FETCH_LOADING_FAIL });

    // const errorObj = e.response?.data?.merchants;
    // if (Object.keys(errorObj || {}).length) {
    //   setError(getErrorValue(errorObj, fieldName));
    // }

    if (e.response?.data?.merchants?.length) {
      setError(e.response?.data?.merchants);
    }
    else {
      errorAlert({
        title:  e.response?.data?.General?.[0]
          || e.response?.data?.title
          || e.response?.data
          || 'Error',
        duration: 0,
        closable: true
      });
    }

    throw new Error(e.response);
  }
};

interface ISetProviderDataV2 {
  dispatch: React.Dispatch<IPaymentsAction>,
  params: any,
  formik: any
}

export const setProvidersV2 = async ({ dispatch, params, formik }: ISetProviderDataV2): Promise<void> => {
  try {
    await PUT({ pathname: `${BASE_URL.PAYMENTS}/providers`, body: params, formik });
  } catch (e) {
    sendSentryMessage({ name: `PUT Set provider error`, context: e });
    dispatch({ type: paymentsActionTypes.FETCH_LOADING_FAIL });
  }
};

interface IProviderLists {
  dispatch: React.Dispatch<IPaymentsAction>,
  providerName: string
}

export const fetchProviderLists = async ({ dispatch, providerName }: IProviderLists): Promise<void> => {
  try {
    dispatch({ type: paymentsActionTypes.SET_PAYMENT_GATEWAY_LISTS_LOADING, payload: true });

    const data = await GET({ pathname: `${BASE_URL.PAYMENTS}/gateways/${providerName}Gateway/GetLists` });

    const lists = {};
    const providersLists = [
      { name: 'CardBrand' },
      { name: 'ProcessorPlatforms' },
      {
        name: 'Countries',
        // value: 'threeLetterCode'
      },
      { name: 'CurrencyEnum' },
      { name: 'Revolv3Processor' },
    ];

    providersLists?.map((item: { name?: string, value?: string }) => {
      if (data && Array.isArray(data?.[item.name])) {
        lists[item.name] = data[item.name]?.map(el => ({
          value: item?.value ? el[item.value] : el,
          label: item?.value ? `${el.name}, ${el[item.value]}` : el

        }));
      } else {
        lists[item.name] = [];
      }
    });

    dispatch({ type: paymentsActionTypes.FETCH_PAYMENT_GATEWAY_LISTS, payload: lists });
  } catch (error) {
    dispatch({ type: paymentsActionTypes.FETCH_PAYMENT_GATEWAY_LISTS, payload: null });
    console.log('fetchProviderLists error', error);
  }
};

export const fetchGatewayRegisterList = async (dispatch: React.Dispatch<IPaymentsAction>): Promise<void> => {
  try {
    dispatch({ type: paymentsActionTypes.SET_PAYMENT_GATEWAY_REGISTER_LIST_LOADING, payload: true });

    const data = await GET({ pathname: `${BASE_URL.PAYMENTS}/gateways/GatewayRegistry/Get` });

    dispatch({ type: paymentsActionTypes.FETCH_PAYMENT_GATEWAY_REGISTER_LIST, payload: data ?? [] });
  } catch (error) {
    console.log('fetchGatewayRegisterList error', error);
  }
};

export const setPaymentGatewayRegister = async (
  dispatch: React.Dispatch<IPaymentsAction>,
  providerName: string,
  formik: any,
  onSuccess: () => void,
): Promise<void> => {
  try {
    if (!formik.isValid) return;
    dispatch({ type: paymentsActionTypes.SET_NEW_PROVIDER_LOADING, payload: true });
    const getParams = (): providerFormReturnTypes => {
      switch (providerName.toLowerCase()) {
        case PROVIDER.ADYEN: return getFormattedAdyenProviderFields(formik.values);
        case PROVIDER.BRAINTREE: return getFormattedBraintreeProviderFields(formik.values);
        case PROVIDER.CHECKOUT: return getFormattedCheckoutProviderFields(formik.values);
        case PROVIDER.NMI: return getFormattedNmiProviderFields(formik.values);
        case PROVIDER.PAYSAFE: return getFormattedPaysafeProviderFields(formik.values);
        case PROVIDER.RAPYD: return getFormattedRapydProviderFields(formik.values);
        case PROVIDER.REVOLV3: return getFormattedRevolv3ProviderFields(formik.values);
        // case PROVIDER.STRIPE: return getFormattedStripeProviderFields(formik.values);
        case PROVIDER.USIO: return getFormattedUsioProviderFields(formik.values);
      }
    };

    await POST({
      pathname: `${BASE_URL.PAYMENTS}/gateways/${providerName}Gateway/Post`,
      body: getParams(),
      formik
    });

    onSuccess();
    dispatch({ type: paymentsActionTypes.SET_NEW_PROVIDER_LOADING, payload: false });
  } catch (e) {
    dispatch({ type: paymentsActionTypes.SET_NEW_PROVIDER_LOADING, payload: false });
    console.error('setPaymentGatewayRegister', e.response);
  }
};

interface IExportCSVProviderData {
  dispatch: React.Dispatch<IPaymentsAction>,
  state: IPaymentsContextState
}

export const exportCSVProviderData = async ({ dispatch, state }: IExportCSVProviderData): Promise<void> => {
  try {
    dispatch({ type: paymentsActionTypes.SET_EXPORT_CSV_LOADING, payload: true });
    const params = getProviderQueryParams(state);

    const data = await GET({
      pathname: `${BASE_URL.PAYMENTS}/providers/export`,
      params,
    });

    if (data) {
      const blob = new Blob([data], { type: 'text/csv' });
      download(blob, 'Providers.csv');
    }

    dispatch({ type: paymentsActionTypes.SET_EXPORT_CSV_LOADING, payload: false });
  } catch (e) {
    console.log(e);
    dispatch({ type: paymentsActionTypes.SET_EXPORT_CSV_LOADING, payload: false });
  }
};

interface IPaymentGatewayTypes {
  dispatch: React.Dispatch<IPaymentsAction>,
  pid: string
}

export const fetchPaymentGatewayTypes = async ({ dispatch, pid }: IPaymentGatewayTypes): Promise<void> => {
  try {
    dispatch({ type: paymentsActionTypes.SET_PAYMENT_GATEWAY_TYPE_LOADING, payload: true });

    const data = await GET({ pathname: `${BASE_URL.PAYMENTS}/gateway/getSupportedGatewayTypes?partnerId=${pid}&isActive=true` });

    let list = [];

    if (data && data?.length) {
      list = data?.map(el => ({
        value: el.name,
        label: el.name
      }));
    }

    dispatch({ type: paymentsActionTypes.PAYMENT_GATEWAY_TYPE, payload: data });
    dispatch({ type: paymentsActionTypes.PROVIDER_PAYMENT_TYPE, payload: list });
  } catch (e) {
    sendSentryMessage({ name: `GET Fetch PaymentGateway types error`, context: e });
    console.log(e);
    dispatch({ type: paymentsActionTypes.PAYMENT_GATEWAY_TYPE, payload: [] });
  }
};

interface IAddNewPaymentGateway {
  dispatch: React.Dispatch<IPaymentsAction>,
  gatewayId: string,
  mid: string
}

export const addNewPaymentGateway = async ({ dispatch, gatewayId, mid }: IAddNewPaymentGateway): Promise<void> => {
  try {
    await POST({
      pathname: `${BASE_URL.PAYMENTS}/gateway?gatewayId=${gatewayId}&mid=${mid}`,
    });

    await fetchPaymentGatewayData({ dispatch, mid });
  } catch (e) {
    sendSentryMessage({ name: `POST Add new payment gateway error`, context: e });
    console.log(e);
  }
};

export const addNewProvider = async (params): Promise<void> => {
  try {
    await axios.post(`${BASE_URL.PAYMENTS}/providers`, params);
  } catch (e) {
    sendSentryMessage({ name: `POST Add new provider error`, context: e });
    console.log(e);
  }
};



export const setProvidersToDefault = (dispatch: React.Dispatch<IPaymentsAction>): void => {
  dispatch({ type: paymentsActionTypes.SET_PROVIDERS_LIST_TO_DEFAULT });
};

interface IQuarantineRelease {
  providerId: string,
  onSuccess: () => void,
  onError: () => void,
}

export const setQuarantineRelease = async ({ providerId, onSuccess, onError }: IQuarantineRelease): Promise<void> => {
  try {
    await POST({ pathname: `${BASE_URL.PAYMENTS}/providers/${providerId}/quarantine-release` });
    onSuccess();
  } catch (e) {
    sendSentryMessage({ name: `PUT Set quarantine release error`, context: e });
    console.log(e);
    onError();
  }
};
